/** Financial allocation reference data, distinct from asset owners and execution teams. */
import type { ColumnDef } from "@tanstack/react-table";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuth } from "@/app/auth";
import {
  type CostCenter,
  useCompleteCostCenters,
  useSaveCostCenter,
} from "@/features/admin/reference/queries";
import { CompleteDataTable } from "@/shared/components/complete-data-table";
import { FormDialog } from "@/shared/components/form-dialog";
import { ErrorState, LoadingState, PageHeader } from "@/shared/components/page";
import { PartyPicker } from "@/shared/components/pickers";
import { useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input } from "@/shared/ui/form";

interface CenterValues extends Record<string, unknown> {
  code: string;
  name: string;
  operating_unit_id: string;
  is_active: boolean;
}

/** Create a stable financial code or maintain its display name/active state. */
export function CostCenterDialog({
  center,
  onClose,
}: {
  center?: CostCenter;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const save = useSaveCostCenter();
  const form = useForm<CenterValues>({
    initial: {
      code: center?.code ?? "",
      name: center?.name ?? "",
      operating_unit_id: center?.operating_unit_id ?? "",
      is_active: center?.is_active ?? true,
    },
    schema: z.object({
      code: z.string().trim().min(1, t("app.required")),
      name: z.string().trim().min(1, t("app.required")),
      operating_unit_id: z.string().min(1, t("app.required")),
    }),
    idPrefix: "cost-center",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit((values) =>
      save.mutateAsync({
        id: center?.id,
        body: center
          ? { name: values.name.trim(), is_active: values.is_active }
          : {
              code: values.code.trim(),
              name: values.name.trim(),
              operating_unit_id: values.operating_unit_id,
              is_active: values.is_active,
            },
      }),
    );
    if (ok) onClose();
  };
  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t(center ? "parties.costCenters.edit" : "parties.costCenters.new")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      <Field id="cost-center-code" label={t("app.code")} error={form.errors.code}>
        <Input {...form.field("code")} disabled={!!center} />
      </Field>
      <Field id="cost-center-name" label={t("app.name")} error={form.errors.name}>
        <Input {...form.field("name")} />
      </Field>
      <Field
        id="cost-center-operating_unit_id"
        label={t("parties.operatingUnit")}
        error={form.errors.operating_unit_id}
        hint={t("parties.costCenters.immutableUnit")}
      >
        {center ? (
          <Input id="cost-center-operating_unit_id" value={center.operating_unit_name} disabled />
        ) : (
          <PartyPicker
            id="cost-center-operating_unit_id"
            role="operator"
            value={form.values.operating_unit_id}
            onChange={(id) => form.set("operating_unit_id", id ?? "")}
          />
        )}
      </Field>
      <Checkbox
        id="cost-center-active"
        label={t("app.active")}
        checked={form.values.is_active}
        onChange={(event) => form.set("is_active", event.target.checked)}
      />
    </FormDialog>
  );
}

/** Manage tenant-scoped cost centers and their internal operating units. */
export function CostCentersPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const centers = useCompleteCostCenters();
  const [dialog, setDialog] = useState<{ center?: CostCenter } | null>(null);
  const columns: ColumnDef<CostCenter>[] = [
    { accessorKey: "code", header: t("app.code") },
    { accessorKey: "name", header: t("app.name") },
    { accessorKey: "operating_unit_name", header: t("parties.operatingUnit") },
    {
      id: "active",
      header: t("app.status"),
      cell: ({ row }) => t(row.original.is_active ? "app.active" : "app.inactive"),
    },
    ...(can("parties.costcenter.manage")
      ? [
          {
            id: "actions",
            header: t("app.actions"),
            cell: ({ row }: { row: { original: CostCenter } }): ReactElement => (
              <Button variant="ghost" size="sm" onClick={() => setDialog({ center: row.original })}>
                {t("app.edit")}
              </Button>
            ),
          },
        ]
      : []),
  ];
  if (centers.isPending) return <LoadingState />;
  if (centers.error) return <ErrorState error={centers.error} />;
  return (
    <>
      <PageHeader
        title={t("parties.costCenters.title")}
        subtitle={t("parties.costCenters.description")}
        actions={
          can("parties.costcenter.manage") ? (
            <Button onClick={() => setDialog({})}>{t("parties.costCenters.new")}</Button>
          ) : null
        }
      />
      <CompleteDataTable
        columns={columns}
        data={centers.data ?? []}
        getSearchText={(center) => `${center.code} ${center.name}`}
        searchDescription={t("tablePilot.searchFields", {
          fields: `${t("app.code")}, ${t("app.name")}`,
        })}
        sortFields={[
          { value: "code", label: t("app.code"), valueOf: (center) => center.code },
          { value: "name", label: t("app.name"), valueOf: (center) => center.name },
        ]}
        minWidth={650}
      />
      {dialog ? <CostCenterDialog center={dialog.center} onClose={() => setDialog(null)} /> : null}
    </>
  );
}
